import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  FirewallAdvancedProtectionLevel,
  FirewallAdvancedRule,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  FirewallAdvancedFormValue,
  FirewallAdvancedSearchableKeywords,
} from '../../../../utils/uiSettings/firewall/firewall.types';
import {
  FIREWALL_ADVANCED_DEFAULT_VALUE,
  getAdvancedProtectionLevelOptionsForDropdown,
  getFirewallAdvancedSearchableKeywords,
} from '../../../../utils/uiSettings/firewall/firewall.values';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzFirewallAdvancedRulesGridWebComponent } from '../gridComponents/GzFirewallAdvancedRulesGridWebComponent';
import {
  FirewallRulesApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';
import { isBrokenRule } from '../utils/validators';
import { InfoBoxSeverity } from '../../../../../../../ui/gzInfoBox/types';
import { classMap } from 'lit/directives/class-map.js';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED)
export class GzFirewallAdvancedWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  FirewallAdvancedFormValue,
  FirewallAdvancedSearchableKeywords,
  FirewallRulesApiClients
> {
  /**
   * Path for Firewall - Rules section.
   */
  protected path = {
    moduleId: ModuleId.FIREWALL,
    sectionId: SectionId.FIREWALL_ADVANCED,
  };

  /**
   * Searchable keywords for Firewall - Rules section.
   */
  protected searchableKeywords = getFirewallAdvancedSearchableKeywords();

  @query('#firewall-advanced-protection-level-form-field')
  private protectionLevelFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#firewall-advanced-create-aggressive-form-field')
  private createAggressiveRuleFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#firewall-advanced-create-form-field')
  private createRuleFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#firewall-advanced-monitor-form-field')
  private monitorFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#firewall-advanced-ignore-form-field')
  private ignoreFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#broken-firewall-rule-warning')
  private brokenFirewallRuleWarning: GzInfoBoxWebComponent;
  @query('#firewall-advanced-rules-grid-form-field')
  private rulesGridFormField: GzFormFieldWebComponent<GzFirewallAdvancedRulesGridWebComponent, FirewallAdvancedRule[]>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Rules].label}"
      ><span>${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Rules].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Configure network access and data traffic rules for applications. The settings only apply to Home/Office and Public profiles.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    const isGeneralSilentModeImpactedByChange =
      PolicyPanelManager.getInstance().isGeneralSilentModeImpactedByFirewallProtectionLevelChange();
    const isGeneralDisplayAlertsImpactedByChange =
      PolicyPanelManager.getInstance().isGeneralDisplayAlertsImpactedByFirewallProtectionLevelChange();
    return html`
      <div>
        <gz-title
          fontSize="11"
          searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Settings].label}"
          fontWeight="600"
          class="form-subtitle"
          >${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Settings].translatedLabel}</gz-title
        >

        <gz-text fontSize="12" fontWeight="400" class="infoText pt-1"
          >${translateFn(
            'Define the firewall decision-making logic when applications request access to network and internet services.'
          )}
        </gz-text>

        <gz-form-field
          searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.ProtectionLevel].label}"
          class="label-mw-250 mt-3"
          id="firewall-advanced-protection-level-form-field"
          label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.ProtectionLevel].translatedLabel}"
        >
          <gz-dropdown
            gz-form-input
            options="${JSON.stringify(getAdvancedProtectionLevelOptionsForDropdown())}"
            @optionSelected="${this.handleProtectionLevelChanged}"
          ></gz-dropdown>
        </gz-form-field>

        <gz-info-box
          id="protection-level-warning"
          severity="${InfoBoxSeverity.INFORMATIVE}"
          class="${classMap({
            hidden: !isGeneralSilentModeImpactedByChange && !isGeneralDisplayAlertsImpactedByChange,
          })} mt-2"
        >
          ${isGeneralSilentModeImpactedByChange
            ? translateFn(
                'Silent Mode is currently enabled. Setting the Firewall protection level to "Ruleset and ask" or "Ruleset, known files and ask" will disable Silent Mode.'
              )
            : translateFn(
                'Do not set the protection level to "Ruleset and ask" or "Ruleset, known files and ask" because alert pop-ups are disabled and these settings will not function correctly.'
              )}
        </gz-info-box>

        <gz-form-field id="firewall-advanced-create-aggressive-form-field" class="mt-2">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.CreateAggressiveRules]
              .label}"
            gz-form-input
            label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.CreateAggressiveRules].translatedLabel}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="firewall-advanced-create-form-field">
          <gz-checkbox
            searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.CreateRules].label}"
            gz-form-input
            label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.CreateRules].translatedLabel}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="firewall-advanced-monitor-form-field">
          <gz-checkbox
            gz-form-input
            searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Monitor].label}"
            label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Monitor].translatedLabel}"
            @change="${e => toggleDisabled(this.ignoreFormField, !e.detail.checked)}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="firewall-advanced-ignore-form-field" class="helper-text">
          <gz-checkbox
            gz-form-input
            searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Ignore].label}"
            label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Ignore].translatedLabel}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-title
          fontSize="11"
          searchable-label="${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Rules].label}"
          fontWeight="600"
          class="form-subtitle"
          id="firewall-advanced-rules-title"
          >${this.searchableKeywords[FirewallAdvancedSearchableKeywords.Rules].translatedLabel}</gz-title
        >

        <gz-info-box id="broken-firewall-rule-warning" severity="warning" class="hidden">
          ${translateFn(
            'Some rules in this policy have been disabled due to a missing application path. Add the required application path and enable them.'
          )}
        </gz-info-box>

        <gz-form-field id="firewall-advanced-rules-grid-form-field">
          <gz-policy-panel-firewall-advanced-rules-grid gz-form-input></gz-policy-panel-firewall-advanced-rules-grid>
        </gz-form-field>
      </div>
    `;
  }

  /**
   * Set clients.
   */
  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'FirewallRules'
    ) as FirewallRulesApiClients;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.firewall.advanced
   */
  protected getValueFromManager(): FirewallAdvancedFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.FIREWALL]: { [SectionId.FIREWALL_ADVANCED]: FIREWALL_ADVANCED_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.FIREWALL]: {
          [SectionId.FIREWALL_ADVANCED]: PolicyPanelManager.getInstance().getPolicy('uiSettings.firewall.advanced'),
        },
      },
    };

    // fix rules if needed
    value.uiSettings.firewall.advanced.rule = this.getFixedRules(value.uiSettings.firewall.advanced.rule);

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  private getFixedRules(rules: FirewallAdvancedRule[]): FirewallAdvancedRule[] {
    // in some cases, enabled property is not set, so we need to set it to true
    return rules.map(rule => {
      return {
        ...rule,
        enabled: typeof rule.enabled === 'boolean' ? rule.enabled : true,
      };
    });
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        firewall: {
          advanced: {
            protectionLevel: this.protectionLevelFormField,
            createAgresiveRules: this.createAggressiveRuleFormField,
            createRulesForAppsBlockedByIDS: this.createRuleFormField,
            monitorProcessChanges: this.monitorFormField,
            ignoreSignedProcesses: this.ignoreFormField,
            rule: this.rulesGridFormField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: this.valueT.uiSettings,
    };

    this.rulesGridFormField.Input.setPolicyId(PolicyPanelManager.getInstance().getPolicyId());
    this.rulesGridFormField.Input.setExportButtonDisabledState();
    toggleVisibility(this.brokenFirewallRuleWarning, this.brokenRulesExist());
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'firewall', 'advanced'], {});
    }

    return value;
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.rulesGridFormField.Input.reset();
  }

  private handleProtectionLevelChanged(e: CustomEvent) {
    if (!this.valueT) {
      return;
    }

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    const oldValue = e.detail.oldValue ? e.detail.oldValue.value : undefined;
    const newValue = e.detail.newValue ? e.detail.newValue.value : undefined;
    // Do nothing if the value is the same
    if (oldValue === newValue) {
      return;
    }

    const shouldUncheckSilentMode =
      PolicyPanelManager.getInstance().isFirewallProtectionLevelValueDependentOfGeneralNotifications(newValue);
    // Do nothing if the protection level is not dependent of silent mode
    if (!shouldUncheckSilentMode) {
      return;
    }

    // Uncheck silent mode
    PolicyPanelManager.getInstance().unsetGeneralDisplaySilentMode();
  }

  private brokenRulesExist(): boolean {
    return !!this.valueT?.uiSettings?.firewall.advanced.rule.some(rule => isBrokenRule(rule));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED: GzFirewallAdvancedWebComponent;
  }
}
